import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";
import type { JsonPullMapping, JsonPullPreview } from "@/utils/jsonMapping";

export type {
  JsonPullMapping,
  JsonPullPreview,
  JsonSpeedField,
  JsonDirField,
  JsonTimeField,
  JsonWindUnit,
  JsonDirFormat,
  JsonTimeMode,
  JsonPick,
} from "@/utils/jsonMapping";

/** Ops metadata (SRF-2748): contact + station details — Postgres, never the KV record. */
export interface StationContact {
  contactName?: string;
  organization?: string;
  /** kite-school | harbor | club | private | partner */
  organizationType?: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
  /** Placement class: beach-mast | buoy | rooftop | harbor | inland */
  stationType?: string;
  /** Device + sensor, e.g. "Ecowitt GW1100 + WS85 sonic" */
  hardware?: string;
  /** Sensor height above ground/water (m); 10 m is the reference */
  mastHeightM?: number;
  /** Surfr spot id this station serves */
  spotId?: number;
  notes?: string;

  // ---- Data-usage consent (read-only here; written via recordConsent) ----
  /** Policy version accepted. Absent => NO consent on record (pre-consent rows). */
  dataConsentVersion?: string;
  /** ISO-8601 acceptance time. */
  dataConsentAt?: string;
  /** IP the acceptance came from — audit evidence. */
  dataConsentIp?: string;
  /** "owner" (accepted it themselves) or "admin:<id>" (recorded on their behalf). */
  dataConsentActor?: string;

  // ---- Pull config (written via updatePull) ----
  /** Upstream URL we FETCH from. Set => this is a pull station, not a push station. */
  pollUrl?: string;
  /** Pull adapter id, e.g. "pioupiou". */
  pollProvider?: string;
  /** Seconds between fetches; 60..1800. */
  pollIntervalS?: number;
  /** Provider-native station id at the upstream (Tempest station_id). */
  pollExternalId?: string;
  /** True when a token is stored. The token itself is NEVER returned by the API. */
  pollAuthTokenConfigured?: boolean;
  /**
   * Field mapping for the `json` pull provider (`poll_options`, contract v1). Required for
   * json, null/absent for every other provider.
   */
  pollOptions?: JsonPullMapping | null;

  // ---- Identity + provenance (SRF-2813). Already served by the admin list; typed
  //      here so the station table can key on the Postgres row rather than the
  //      Cloudflare snapshot id, which encodes the ingest method.
  /** "pull" | "push". A row is a pull station iff pollUrl is set; this is the explicit form. */
  ingestMode?: string;
  /** Set when a station owner registered it themselves; absent for ops-created rows. */
  ownerUserId?: number;
  /** pending | active | revoked — the Postgres-side approval state. */
  requestStatus?: string;
}

/**
 * One pull format as described by the backend catalogue
 * (`GET /api/admin/push-stations/pull-providers`, SRF-2869).
 *
 * This is the ONLY source of truth for which providers exist and what each needs — the UI
 * must never hard-code a provider list again (the old `PULL_PROVIDERS` constant went stale
 * the moment the backend gained a third adapter, and a stale list silently hides a format
 * ops can actually use).
 */
export interface PullProvider {
  /** Adapter id persisted as `pollProvider`, e.g. "clientraw". */
  id: string;
  /** Human label for the source card, e.g. "Weather Display (clientraw.txt)". */
  label: string;
  /** One or two sentences explaining what this format is, shown under the label. */
  description: string;
  /** Example URL for the URL field's placeholder. */
  urlPlaceholder: string;
  /** True when the owner's upstream token is required. */
  needsAuthToken: boolean;
  /** True when the provider's own station id is required alongside the URL. */
  needsExternalId: boolean;
  /** Field label for the external id, e.g. "Tempest station id". Null when unused. */
  externalIdLabel: string | null;
  /** Lower bound for this format's poll interval, in seconds. */
  minIntervalS: number;
  /** What to prefill the interval with for this format, in seconds. */
  defaultIntervalS: number;
  /**
   * True when the format needs a field mapping (`pollOptions`) — the `json` provider. The
   * wizard then embeds the JsonMappingConfigurator in the Connection step. Optional so an
   * older backend (no json provider) keeps working.
   */
  needsOptions?: boolean;
  /** Preset mapping for needsOptions formats ("Surfr standard"). */
  defaultOptions?: JsonPullMapping | null;
}

/**
 * Upper polling bound. Unlike the lower bound (per-format, from the catalogue) this one is
 * global — the backend API and a DB CHECK constraint both cap at 1800 s.
 */
export const MAX_POLL_INTERVAL_S = 1800;

/** One decoded observation from `POST /pull-test` — enough to prove the URL parses. */
export interface PullTestSample {
  windMs: number | null;
  gustMs: number | null;
  dirDeg: number | null;
  /**
   * The observation time (fetch time for formats without their own clock). The contract
   * says ISO-8601 UTC; older backends sent epoch millis — accept both (see toEpochMs).
   */
  observedAt: number | string | null;
  lat?: number | null;
  lon?: number | null;
}

export interface PullTestRequest {
  provider: string;
  url: string;
  externalId?: string | null;
  /**
   * A NEW token to test with. OMIT the key entirely to test against whatever is stored
   * server-side for `stationRef` — never send null or "" to mean "use the stored one":
   * the backend cannot tell that apart from "this source needs no token", which is what
   * made a working authenticated configuration untestable from the edit flow.
   */
  token?: string;
  /**
   * Existing station whose stored token the backend should fall back to: the credential
   * prefix, or the station id for pull stations (the same identifier `updatePull` is
   * addressed by). Only meaningful when editing — a station being registered has nothing
   * stored yet.
   */
  stationRef?: string;
  /**
   * Field mapping to test with (`json` provider only). OMIT to have the backend fetch the
   * document and return a `preview.suggested` guess — that is the configurator's "Fetch".
   */
  options?: JsonPullMapping;
  /**
   * Station location (json provider). When given, the backend fills
   * `preview.suggested.time.tz` from it whenever the suggestion is local time.
   */
  lat?: number;
  lon?: number;
}

/**
 * Result of a test fetch. NOTE: a failed test is HTTP 200 with `ok:false` — the request
 * itself succeeded, the upstream is what failed. Never treat this as an exception path.
 */
export interface PullTestResult {
  ok: boolean;
  sample?: PullTestSample | null;
  /** A fixed error code (`stale`, `http_404`, …) or, from older backends, a readable message. */
  error?: string | null;
  /**
   * `json` provider only: the picked record + guess, present even when `ok` is false as
   * long as the body parsed as JSON — the configurator builds its field list from it.
   */
  preview?: JsonPullPreview | null;
}

/** m/s -> knots. The wizard shows wind in knots because the rest of the admin does. */
export const MS_TO_KNOTS = 1.94384;

export interface UpdatePullRequest {
  /** Empty/omitted CLEARS pull config (station reverts to push-only). */
  pollUrl?: string;
  pollProvider?: string;
  pollIntervalS?: number;
  /** Provider-native station id. Required for AUTHENTICATED_PULL_PROVIDERS. */
  pollExternalId?: string;
  /**
   * Owner's upstream token. WRITE-ONLY: absent means "keep whatever is stored" — it
   * cannot be read back to echo, so absence must never be read as "clear".
   */
  pollAuthToken?: string;
  /** The explicit way to remove a stored token (see pollAuthToken). */
  clearPollAuthToken?: boolean;
  /** Field mapping — required for the `json` provider, must be omitted for every other one. */
  pollOptions?: JsonPullMapping;
}

export const ORGANIZATION_TYPES = ["kite-school", "harbor", "club", "private", "partner"] as const;
export const STATION_TYPES = ["beach-mast", "buoy", "rooftop", "harbor", "inland"] as const;

/** KV credential record as returned by the admin API (never contains the token). */
export interface PushStationCredential {
  /** Credential prefix. NULL for PULL stations, which have no credential — address those
   *  by stationId instead (see stationRef in PushStationsPage). */
  prefix: string | null;
  stationId: string;
  name: string;
  lat: number;
  lon: number;
  /** "pending" = SRF-2750 self-service request awaiting admin approval. */
  status: "active" | "revoked" | "pending";
  ratePerMin?: number;
  contact?: StationContact | null;
  /**
   * The id this station's observations are published under in the live-wind snapshot,
   * resolved by the BACKEND (`push_<id>` / `<provider>_<id>`).
   *
   * Read this instead of re-deriving it. The UI used to build `${provider}_${externalId}`
   * itself, which is right for Tempest and Pioupiou and wrong for any format whose external
   * id is its poll URL (clientraw) — those publish under our own station id, so the row
   * looked up a key that is never written and read "no data" forever. Optional so an older
   * backend still works: the caller falls back to the old derivation. (SRF-2869.)
   */
  canonicalStationId?: string | null;
  /** SRF-2749 live ingest status — present only when fetched with `withStatus`. */
  lastObsMs?: number;
  stale?: boolean;
  windKts?: number;
  windDir?: number;
  gustKts?: number;
  /**
   * Epoch ms until which a rotated-out token of this station still works (rotation grace),
   * or null/absent when none is in grace. A value in the future means the credential was
   * changed less than 5 minutes ago.
   */
  graceUntil?: number | null;
}

/** One station from the Cloudflare snapshot (full.json via /windystations/all) —
 *  covers pull providers (metar/ndbc/knmi/pws) AND push stations. */
export interface SnapshotStation {
  id: string;
  name: string;
  lat: number;
  lon: number;
  source: string;
  windKts: number | null;
  windDir: number | null;
  gustKts: number | null;
  obsTs: number | null;
  stale: boolean;
}

export type CredentialMode = "generate" | "passkey" | "pull";

export interface CreatePushStationRequest {
  stationId: string;
  name: string;
  lat: number;
  lon: number;
  /**
   * Declared credential mode. "pull" registers a credential-less station whose data WE
   * fetch (config in `pull`); the backend never infers pull from the other fields.
   * Omitted = generate/passkey by the presence of `passkey` (legacy clients).
   */
  credentialMode?: CredentialMode;
  /** Ecowitt mode: the device PASSKEY is the token. Empty -> backend generates one. */
  passkey?: string;
  ratePerMin?: number;
  contact?: StationContact;
  /** Pull mode only: same shape and validation as updatePull. */
  pull?: UpdatePullRequest;
}

/** Editable slice of the KV credential record — stationId is deliberately absent. */
export interface UpdateStationRequest {
  name: string;
  lat: number;
  lon: number;
  /** Omitted / undefined clears the override and the worker default applies. */
  ratePerMin?: number;
}

/** What a delete actually removed (SRF-2803): the KV credential and the Postgres row.
 *  BigQuery history is retained. */
export interface DeletePushStationResult {
  prefix: string | null;
  stationId: string;
  /** True when the Postgres row is gone. False = KV credential removed but the row
   *  survived, so the station id is still taken. */
  contactDeleted: boolean;
}

export interface CreatePushStationResult {
  /** Absent for pull stations — they have no credential. */
  credential?: PushStationCredential;
  stationId?: string;
  credentialMode?: CredentialMode;
  /** Present ONLY for generated tokens — shown once, never retrievable again. */
  token?: string;
  /** False when the credential was created but the registry row failed to persist. */
  contactSaved?: boolean;
  /**
   * Credential replacement only: epoch ms until which the rotated-out token keeps working.
   * Null/absent when the old token stopped at once (pending station, same-prefix PASSKEY).
   */
  graceUntil?: number | null;
}

// --- rejected-push inspector ------------------------------------------------------------

/** One aggregated credential reject: (protocol, path, token hash, ip). Never the token. */
export interface PushReject {
  protocol: string;
  path: string;
  tokenSha256: string | null;
  /** Public credential prefix (first 10 chars) — null when no/short token. */
  tokenPrefix: string | null;
  ip: string;
  country: string | null;
  userAgent: string | null;
  /** WU `ID` the device sent (station hint). */
  hintWuId: string | null;
  /** Windguru `uid` the device sent (station hint). */
  hintWindguruUid: string | null;
  status: number;
  reason: string;
  count: number;
  firstSeen: number;
  lastSeen: number;
  /** Per-IP overflow bucket (too many distinct tokens from one IP). */
  overflow: boolean;
}

export interface CaptureMatch {
  tokenPrefix?: string;
  ip?: string;
  tokenSha256?: string;
}

export interface CaptureRule {
  match: CaptureMatch;
  createdAt: number;
  endsAt: number;
  maxCaptures: number;
  taken: number;
}

export interface PushRejectsResponse {
  now: number;
  rejects: PushReject[];
  rule: CaptureRule | null;
  captureCount: number;
}

/** A captured rejected request. Secrets are masked unless `revealed`. */
export interface PushCapture {
  id: number;
  ts: number;
  expiresAt: number;
  protocol: string;
  reason: string;
  tokenPrefix: string | null;
  tokenSha256: string | null;
  ip: string;
  method: string;
  path: string;
  query: string;
  headers: Record<string, string>;
  body: string | null;
  revealed: boolean;
  /** Only on a revealed capture: the token its protocol carries — what "use as credential" adopts. */
  token?: string | null;
}

export interface PushCapturesResponse {
  now: number;
  rule: CaptureRule | null;
  captures: PushCapture[];
}

export const PushStationService = {
  async fetchRejects(sinceMs?: number): Promise<PushRejectsResponse> {
    return requestHandler<PushRejectsResponse>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.PUSH_STATIONS.REJECTS, {
        params: sinceMs != null ? { sinceMs } : undefined,
      });
      return response.data;
    }, "PushStationService -> fetchRejects");
  },

  async startCapture(match: CaptureMatch, minutes: number, maxCaptures = 10): Promise<{ rule: CaptureRule }> {
    return requestHandler<{ rule: CaptureRule }>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.REJECTS_CAPTURE, {
        match,
        minutes,
        maxCaptures,
      });
      return response.data;
    }, "PushStationService -> startCapture");
  },

  /** Masked captures; `revealId` unmasks exactly one (audited server-side). */
  async fetchCaptures(revealId?: number): Promise<PushCapturesResponse> {
    return requestHandler<PushCapturesResponse>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.PUSH_STATIONS.REJECTS_CAPTURES, {
        params: revealId != null ? { reveal: revealId } : undefined,
      });
      return response.data;
    }, "PushStationService -> fetchCaptures");
  },

  async purgeCaptures(): Promise<{ deleted: number }> {
    return requestHandler<{ deleted: number }>(async () => {
      const response = await axiosInstance.delete(ENDPOINTS.PUSH_STATIONS.REJECTS_CAPTURES);
      return response.data;
    }, "PushStationService -> purgeCaptures");
  },

  async fetchAll(withStatus = false): Promise<PushStationCredential[]> {
    return requestHandler<PushStationCredential[]>(async () => {
      const response = await axiosInstance.get(
        withStatus ? `${ENDPOINTS.PUSH_STATIONS.LIST}?status=true` : ENDPOINTS.PUSH_STATIONS.LIST
      );
      return response.data;
    }, "PushStationService -> fetchAll");
  },

  /** Every station served by the Cloudflare live-wind plane (admin passthrough of the
   *  worker's /windystations/all — the worker itself has no CORS). */
  async fetchAllCloudflare(): Promise<{ generation: number; stations: SnapshotStation[] }> {
    return requestHandler<{ generation: number; stations: SnapshotStation[] }>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.PUSH_STATIONS.CLOUDFLARE_ALL);
      return response.data;
    }, "PushStationService -> fetchAllCloudflare");
  },

  async create(req: CreatePushStationRequest): Promise<CreatePushStationResult> {
    return requestHandler<CreatePushStationResult>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.CREATE, req);
      return response.data;
    }, "PushStationService -> create");
  },

  async revoke(prefix: string): Promise<PushStationCredential> {
    return requestHandler<PushStationCredential>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.REVOKE(prefix));
      return response.data;
    }, "PushStationService -> revoke");
  },

  /**
   * Permanently delete a credential (SRF-2803). Irreversible — prefer `revoke`, which stops
   * the station reversibly and keeps the record.
   *
   * `stationId` is a required confirmation: the backend refuses the delete unless it matches
   * the record actually stored at this prefix, so a stale list cannot remove the wrong
   * station. Observations in BigQuery and the contact/consent row are retained.
   */
  async delete(prefix: string, stationId: string): Promise<DeletePushStationResult> {
    return requestHandler<DeletePushStationResult>(async () => {
      const response = await axiosInstance.delete(ENDPOINTS.PUSH_STATIONS.DELETE(prefix), {
        data: { stationId },
      });
      return response.data;
    }, "PushStationService -> delete");
  },

  async reactivate(prefix: string): Promise<PushStationCredential> {
    return requestHandler<PushStationCredential>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.REACTIVATE(prefix));
      return response.data;
    }, "PushStationService -> reactivate");
  },

  /** SRF-2750: approve a pending self-service station (KV status -> active). */
  async approve(prefix: string): Promise<PushStationCredential> {
    return requestHandler<PushStationCredential>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.APPROVE(prefix));
      return response.data;
    }, "PushStationService -> approve");
  },

  /** SRF-2750: reject a pending self-service station with an optional reason. */
  async reject(prefix: string, reason?: string): Promise<PushStationCredential> {
    return requestHandler<PushStationCredential>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.PUSH_STATIONS.REJECT(prefix),
        reason ? { reason } : {}
      );
      return response.data;
    }, "PushStationService -> reject");
  },

  /**
   * Edit the station's own record: display name, coordinates, rate limit. The credential
   * and the status are untouched by this call. `stationId` is immutable server-side — it
   * is the observation identity used by the worker and BigQuery history.
   */
  async updateStation(
    prefix: string,
    fields: UpdateStationRequest
  ): Promise<PushStationCredential> {
    return requestHandler<PushStationCredential>(async () => {
      const response = await axiosInstance.put(ENDPOINTS.PUSH_STATIONS.STATION(prefix), fields);
      return response.data;
    }, "PushStationService -> updateStation");
  },

  /**
   * Replace the push credential. Pass a `passkey` to bind a device PASSKEY, or omit it to
   * have the backend generate an `lw_` token — returned once in `token` and never again.
   * The station keeps its current status; the old credential stops working immediately.
   */
  async replaceCredential(
    prefix: string,
    passkey?: string,
    options: { verbatim?: boolean } = {}
  ): Promise<CreatePushStationResult> {
    return requestHandler<CreatePushStationResult>(async () => {
      // `verbatim` (admin adoption of an observed device token): stored byte-for-byte, no
      // trimming or Ecowitt upper-casing.
      const response = await axiosInstance.post(
        ENDPOINTS.PUSH_STATIONS.CREDENTIAL(prefix),
        passkey ? (options.verbatim ? { passkey, verbatim: true } : { passkey }) : {}
      );
      return response.data;
    }, "PushStationService -> replaceCredential");
  },

  async updateContact(prefix: string, contact: StationContact): Promise<StationContact> {
    return requestHandler<StationContact>(async () => {
      const response = await axiosInstance.put(ENDPOINTS.PUSH_STATIONS.CONTACT(prefix), contact);
      return response.data;
    }, "PushStationService -> updateContact");
  },

  /**
   * Configure (or clear) pull ingest — the station whose data we FETCH from a URL.
   * Separate from updateContact because the backend validates it: https only, a known
   * provider, and an interval inside 60..1800s.
   */
  async updatePull(prefix: string, fields: UpdatePullRequest): Promise<StationContact> {
    return requestHandler<StationContact>(async () => {
      const response = await axiosInstance.put(ENDPOINTS.PUSH_STATIONS.PULL(prefix), fields);
      return response.data;
    }, "PushStationService -> updatePull");
  },

  /**
   * The pull-format catalogue (SRF-2869). Drives every per-format decision in the wizard:
   * which fields to show, the URL placeholder, and the interval bounds/default.
   */
  async listPullProviders(): Promise<PullProvider[]> {
    return requestHandler<PullProvider[]>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.PUSH_STATIONS.PULL_PROVIDERS);
      return response.data?.providers ?? [];
    }, "PushStationService -> listPullProviders");
  },

  /**
   * Try a poll URL before saving it: one bounded upstream fetch, parsed with the format's
   * sniffer. Nothing is persisted and the token is never echoed back.
   *
   * A failing upstream is reported as `{ok:false, error}` with HTTP 200 — only a broken
   * request to OUR backend throws.
   */
  async testPull(req: PullTestRequest): Promise<PullTestResult> {
    return requestHandler<PullTestResult>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.PULL_TEST, req);
      return response.data;
    }, "PushStationService -> testPull");
  },

  /**
   * IANA time zone for a location, from the backend's PostGIS `timezone_areas` lookup.
   * `null` when the point is in no zone polygon (open sea) — the caller falls back to the
   * browser zone then.
   */
  async lookupTimezone(lat: number, lon: number): Promise<string | null> {
    return requestHandler<string | null>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.PUSH_STATIONS.TIMEZONE, {
        params: { lat, lon },
      });
      const tz = response.data?.tz;
      return typeof tz === "string" && tz ? tz : null;
    }, "PushStationService -> lookupTimezone");
  },

  /**
   * Record data-usage consent on the owner's behalf (they agreed by email/contract and an
   * admin is setting the station up for them). Stored actor is "admin:<id>", never "owner" —
   * the audit trail must always show which of the two it was. `evidence` is required.
   */
  async recordConsent(prefix: string, evidence: string): Promise<StationContact> {
    return requestHandler<StationContact>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.PUSH_STATIONS.CONSENT(prefix), {
        evidence,
      });
      return response.data;
    }, "PushStationService -> recordConsent");
  },
};
